import React, { useEffect, useState } from 'react';
import { G, styles, api } from '../theme.js';
import { Btn, Field, Input, Spinner } from '../ui.jsx';

// Centered card layout shared across all auth-flow pages.
function Shell({ title, subtitle, children }) {
  return <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', padding:20, background:`radial-gradient(circle at 30% 20%, ${G.purpleDim}, transparent 50%), ${G.bg}` }}>
    <div style={{ ...styles.card, width:'100%', maxWidth:420, padding:32 }}>
      <div style={{ textAlign:'center', marginBottom:22 }}>
        <a href="/" style={{ textDecoration:'none', fontSize:24, fontWeight:800, background:`linear-gradient(135deg,${G.purple},${G.cyan})`, WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>TradeOps</a>
      </div>
      <h2 style={{ margin:'0 0 6px', fontSize:22 }}>{title}</h2>
      {subtitle && <p style={{ color:G.textMuted, fontSize:14, marginTop:0, marginBottom:18 }}>{subtitle}</p>}
      {children}
    </div>
  </div>;
}

export function VerifyEmail() {
  const [state, setState] = useState('checking'); // checking | success | error
  const [err, setErr] = useState(null);
  const token = new URLSearchParams(window.location.search).get('token');

  useEffect(() => {
    if (!token) { setState('error'); setErr('Missing token.'); return; }
    api(`/api/auth/verify-email?token=${encodeURIComponent(token)}`)
      .then(() => setState('success'))
      .catch(e => { setState('error'); setErr(e.message); });
  }, [token]);

  if (state === 'checking') return <Shell title="Verifying…"><Spinner/></Shell>;
  if (state === 'success') return <Shell title="✓ Email verified" subtitle="You're all set. Head to your dashboard.">
    <Btn v="primary" full onClick={()=>window.location.href='/'}>Go to dashboard</Btn>
  </Shell>;
  return <Shell title="Couldn't verify" subtitle={err}>
    <Btn v="ghost" full onClick={()=>window.location.href='/login'}>Back to sign in</Btn>
  </Shell>;
}

export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api('/api/auth/forgot-password', { method:'POST', body:{ email } });
      setSent(true);
    } catch {} // intentionally generic — we don't leak which emails exist
    finally { setBusy(false); }
  };

  if (sent) return <Shell title="Check your inbox" subtitle={`If an account exists for ${email}, we sent a reset link. It expires in 1 hour.`}>
    <Btn v="ghost" full onClick={()=>window.location.href='/login'}>Back to sign in</Btn>
  </Shell>;

  return <Shell title="Forgot password?" subtitle="Enter your email and we'll send you a reset link.">
    <form onSubmit={submit}>
      <Field label="Email"><Input type="email" value={email} onChange={e=>setEmail(e.target.value)} required autoFocus/></Field>
      <Btn v="primary" sz="md" type="submit" full disabled={busy}>{busy ? <Spinner/> : 'Send reset link'}</Btn>
    </form>
    <div style={{ textAlign:'center', marginTop:14, fontSize:13 }}>
      <a href="/login" style={{ color:G.purple, textDecoration:'none', fontWeight:600 }}>← Back to sign in</a>
    </div>
  </Shell>;
}

export function ResetPassword() {
  const token = new URLSearchParams(window.location.search).get('token');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) return setErr('Password must be at least 6 characters.');
    if (pw !== pw2) return setErr('Passwords don\'t match.');
    setErr(null);
    setBusy(true);
    try {
      await api('/api/auth/reset-password', { method:'POST', body:{ token, password: pw } });
      setDone(true);
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  if (!token) return <Shell title="Invalid link"><Btn v="ghost" full onClick={()=>window.location.href='/login'}>Back to sign in</Btn></Shell>;
  if (done) return <Shell title="✓ Password updated" subtitle="You can sign in with your new password now.">
    <Btn v="primary" full onClick={()=>window.location.href='/login'}>Sign in</Btn>
  </Shell>;

  return <Shell title="Set a new password">
    <form onSubmit={submit}>
      <Field label="New password"><Input type="password" value={pw} onChange={e=>setPw(e.target.value)} required minLength={6} autoComplete="new-password"/></Field>
      <Field label="Confirm new password"><Input type="password" value={pw2} onChange={e=>setPw2(e.target.value)} required minLength={6} autoComplete="new-password"/></Field>
      {err && <div style={{ color:G.red, fontSize:13, marginBottom:8 }}>{err}</div>}
      <Btn v="primary" sz="md" type="submit" full disabled={busy}>{busy ? <Spinner/> : 'Update password'}</Btn>
    </form>
  </Shell>;
}
